import storeManager from '../../store/StoreManager';
import sessionReader from './SessionReader';
import numberingSchemes from '../../constants/NumberingSchemes';
import testTypes from '../../constants/TestTypes';
import customDynamicCdns from '../../constants/CustomDynamicCdns';

import WebRTCRecordingStreamConstants from '../../constants/WebRTCRecordingStreamConstants';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import questionReader from './QuestionReader';
import customFieldsService from '../../services/CustomFieldsService';
import customFieldsConstants from '../../constants/CustomFieldsConstants';
import marketPlaces from '../../constants/MarketPlaces';

/*
    "auto_deliver": boolean,
    "auto_request_plagiarism_control": boolean,
    "custom_lost_connection_alert_message": string
    "enable_alternative_feedback": boolean, // Whether or not to show feedback info for alternatives
    "enable_attached_sketches": boolean,
    "enable_candidate_highlighting": boolean,
    "enable_flagging": boolean,
    "enable_new_player": boolean,
    "enable_sketches": boolean,
    "hide_assessmentTrackDiv": boolean,
    "hide_question_titles": boolean,
    "lockdown_browser": boolean,
    "numbering_scheme": string, // sectionAlphaNumeric, sectionAlphaNumeric, testSequential, sectionNumeric
    "oral_exam": boolean,
    "qti2_hideResultpage": boolean, // Whether or not to show result page directly after delivery.
    "qti2_notNavigateBack": boolean,
    "qti2_student_preview": boolean, // Whether or not to make delivery open for review by student after delivery. (Link from Dashboard)
    "qti2_student_preview_full_feedback": boolean, // Whether or not to show results and correct answers for student review.
    "require_day_password": boolean,
    "show_correct_answers": boolean,
    "show_lost_connection_alert": boolean,
    "show_max_marks": boolean,
    "show_section": boolean,
    "spell_checker_disabled": boolean,
    "start_test_manually": boolean,
    "test_player_style": string, // default, primary_school_1, primary_school_2, primary_school_3
    "timeRemainingHidden": boolean,
*/

const settingsReader = {
    getSettingValue(settingName) {
        const { settings } = storeManager.getState();
        return settings?.values?.[settingName];
    },

    hasSettingValue(settingName, expectedValue, settings) {
        const settingsToUse = settings || storeManager.getState().settings;
        return settingsToUse.values && settingsToUse.values[settingName] === expectedValue;
    },

    getMarketplacePropertyValue(settingName, settings = storeManager.getState().settings) {
        const marketplaceProperties = { ...settings.marketplaceProperties, ...window.marketplaceProperties };
        return marketplaceProperties[settingName];
    },

    hasMarketplacePropertyValue(settingName, expectedValue) {
        return this.getMarketplacePropertyValue(settingName) === expectedValue;
    },

    isDeliverBlankEnabled(settings, api) {
        const state = storeManager.getState();
        const usedApi = api || state.api;
        return this.hasMarketplacePropertyValue('IA_DELIVER_BLANK_ENABLED', true, settings || state.settings)
            && usedApi.isOnline;
    },

    isCandidateLevelDurationEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_CANDIDATE_LEVEL_DURATION', true);
    },

    isPBRenderStyleEnabled() {
        return this.hasMarketplacePropertyValue('PB_RENDER_STYLE', 'ceq');
    },

    isEssayToolbarPresetEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_PRESET_ESSAY_TOOLBAR', true);
    },

    isEssaySpecialCharsEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_PRESET_ESSAY_SPECIAL_CHARS', true);
    },

    isMathSymbolPalettePresetEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_PRESET_MATH_SYMBOL_PALETTE', true);
    },

    isTTSEnabled(settings) {
        return this.hasSettingValue('enable_tts', true, settings || storeManager.getState().settings);
    },

    isSEBForIOSEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_SEB_IOS', true);
    },

    getPlayerAllowedResourceLoadTimeMinutes() {
        return this.getMarketplacePropertyValue('PLAYER_ALLOWED_RESOURCE_LOAD_TIME_MINUTES');
    },

    getPlayerMediaPresignedUrlLifetimeHours() {
        return this.getMarketplacePropertyValue('PLAYER_MEDIA_ITEMS_PRESIGNED_URL_LIFETIME') || 5;
    },

    isMathJax3Enabled() {
        return this.hasMarketplacePropertyValue('ENABLE_MATHJAX3_RENDER', true);
    },

    shouldUsePresignedUrl() {
        return this.hasMarketplacePropertyValue('PLAYER_USE_PRESIGNED_URL', true)
            || (sessionReader.isContextThumbnail());
    },

    isExpandableTextFieldEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_EXPANDABLE_TEXT_FIELD', true);
    },

    isCandidateDrawingOptionEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_CANDIDATE_DRAWING_OPTION', true);
    },

    isSaveDrawingAsUriEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_SAVE_DRAWING_AS_URI', true);
    },

    /**
     * Checks if drawings should be stored as S3 URIs. Requires both MPP flag and test-level item property to be enabled.
     */
    shouldStoreDrawingsAsUri() {
        const isMppEnabled = this.isSaveDrawingAsUriEnabled();
        if (!isMppEnabled) {
            return false;
        }

        const state = storeManager.getState();
        if (!state?.settings) {
            return false;
        }

        const storeDrawingsAsUri = this.getSettingValue('store_drawings_as_uri');
        return !!storeDrawingsAsUri;
    },

    getDrawingAutoSaveFrequency() {
        const frequency = this.getMarketplacePropertyValue('DRAWING_AUTO_SAVE_FREQUENCY');
        const numericFrequency = Number.parseInt(frequency, 10);
        // Return null if invalid, otherwise return the frequency (treating 1-9 as 30)
        if (!numericFrequency || numericFrequency <= 0 || Number.isNaN(numericFrequency)) {
            return null;
        }
        if (numericFrequency >= 1 && numericFrequency <= 9) {
            return 30;
        }
        return numericFrequency;
    },

    isFlaggingEnabled(settings) {
        return this.hasSettingValue('enable_flagging', true, settings || storeManager.getState().settings);
    },

    isShowMaxMarksEnabled(settings) {
        return this.hasSettingValue('show_max_marks', true, settings || storeManager.getState().settings);
    },

    isShowQuestionTitlesEnabled(settings) {
        return this.hasSettingValue('hide_question_titles', false, settings || storeManager.getState().settings);
    },

    isShowSectionEnabled(settings) {
        return this.hasSettingValue('show_section', true, settings || storeManager.getState().settings);
    },

    isCandidateHighlightingEnabled(settings = storeManager.getState().settings) {
        return this.hasSettingValue('enable_candidate_highlighting', true, settings);
    },

    isWithdrawEnabled(settings) {
        return this.hasMarketplacePropertyValue('IA_ENABLE_WITHDRAW', true, settings || storeManager.getState().settings);
    },

    isInvigilatorPasswordRequiredForDeliverBlank(settings) {
        return this.hasMarketplacePropertyValue('IA_DELIVER_BLANK_INVIGILATOR_PASSWORD', true, settings || storeManager.getState().settings);
    },

    isInvigilatorPasswordToResumeTestDisabled() {
        return this.hasMarketplacePropertyValue('DISABLE_INVIGILATOR_PASSWORD_TO_RESUME_OR_QUIT_TEST', true);
    },

    showAssessmentTrack(settings) {
        return this.hasSettingValue('hide_assessmentTrackDiv', false, settings || storeManager.getState().settings);
    },

    allowNavigateBack(settings) {
        return this.hasSettingValue('qti2_notNavigateBack', false, settings || storeManager.getState().settings);
    },

    isTimeRemainingHidden(settings = storeManager.getState().settings) {
        return this.hasSettingValue('timeRemainingHidden', true, settings);
    },

    showTableOfContents(settings) {
        return this.hasSettingValue('show_table_of_contents', true, settings || storeManager.getState().settings);
    },

    showAccessibilityHelpers(settings) {
        return this.hasSettingValue('show_accessibility_helpers', true, settings || storeManager.getState().settings);
    },

    showCorrectResponsesIfAvailable(settings) {
        return !this.hasSettingValue('show_correct_answers', false, settings || storeManager.getState().settings);
    },

    showCorrectResponsesPostDelivery(settings) {
        return this.hasSettingValue('qti2_student_preview_full_feedback', true, settings || storeManager.getState().settings);
    },

    shouldSectionPageBreakOnPrintView() {
        return this.hasSettingValue(sessionReader.printParamNames.INSERT_PAGE_BREAK_ON_NEW_SECTION, true);
    },

    areAttachedSketchesEnabled(settings) {
        return this.hasSettingValue('enable_attached_sketches', true, settings || storeManager.getState().settings);
    },

    /**
     * Checks in the marketplace property to enable feedback support is on.
     * @param settings
     * @returns {boolean}
     */
    isFeedbackEnabledInMarketplace(settings) {
        return this.hasMarketplacePropertyValue('IA_OFFER_ENABLE_FEEDBACK', true, settings || storeManager.getState().settings);
    },

    isServiceWorkerCacheEnabled(settings) {
        return this.hasMarketplacePropertyValue('ENABLE_PLAYER_SERVICE_WORKER_CACHE', true, settings || storeManager.getState().settings);
    },

    shouldCacheResourcesBeforeTestStart(settings = storeManager.getState().settings) {
        return this.hasSettingValue('preload_test_before_start', true, settings || storeManager.getState().settings);
    },

    /**
     * Checks if the particular Test has support for showing feedback for alternative.
     * @param settings
     * @returns {boolean}
     */
    isFeedbackForAlternativeEnabled(settings) {
        return this.hasSettingValue('enable_alternative_feedback', true, settings || storeManager.getState().settings);
    },

    /**
     * Checks if the particular Test has support for showing feedback for question.
     * @param settings
     * @returns {boolean}
     */
    isFeedbackForQuestionEnabled(settings = storeManager.getState().settings) {
        return this.hasSettingValue('enable_question_feedback', true, settings);
    },

    isInteractionFeedbackEnabled(settings = storeManager.getState().settings) {
        return this.hasSettingValue('enable_interaction_feedback', true, settings);
    },

    isShowResultsAndCorrectAnswersForStudentReviewEnabled(settings = storeManager.getState().settings) {
        return this.hasSettingValue('qti2_student_preview_full_feedback', true, settings);
    },

    isShowMarksOnlySelectedForStudentReviewEnabled(settings = storeManager.getState().settings) {
    // in the settings we have used candidateSubmissionAccessLevel, so instead of candidate_submission_access_level we can use it
        return this.hasSettingValue('candidate_submission_access_level', 'marks_only', settings);
    },
    isReceiptModeActive(settings = storeManager.getState().settings) {
        return this.hasSettingValue('qti2_student_preview', true, settings);
    },

    showLostConnectionAlert(settings = storeManager.getState().settings) {
        return this.hasSettingValue('show_lost_connection_alert', true, settings);
    },

    getCustomLostConnectionAlertMessage(settings = storeManager.getState().settings) {
        return settings.values.custom_lost_connection_alert_message?.replace(/\\"/g, '"');
    },

    hideNextButtonUntilAnswered(settings = storeManager.getState().settings) {
        return this.hasSettingValue('hide_next_button_until_question_answered', true, settings) || sessionReader.isContextReadinessCheck();
    },

    /**
     * Gets the maximum allowed file upload size in bytes.
     * @param settings
     * @returns {int}
     */
    getMaxFileUploadSize({ settings } = storeManager.getState()) {
        let megabytes = parseInt(settings.marketplaceProperties.S3_FILE_SIZE_THRESHOLD_MB, 10);
        megabytes = isNaN(megabytes) ? 1024 : megabytes;

        return parseInt(megabytes, 10) * 1024 * 1024;
    },

    getRewindAudioTimeout({ settings } = storeManager.getState()) {
        let timeout = parseInt(settings.marketplaceProperties.REWIND_AUDIO_TIMEOUT, 10);
        timeout = isNaN(timeout) ? 5000 : timeout;

        return parseInt(timeout, 10);
    },

    getSettingsForSection(sectionId, { settings } = storeManager.getState()) {
        const sectionsSettings = settings.values.section_settings || {};
        return sectionsSettings[sectionId] || {};
    },

    getNumberingScheme({ settings } = storeManager.getState()) {
        return settings.values.numbering_scheme;
    },

    useSectionAlphaNumeric() {
        return this.getNumberingScheme() === numberingSchemes.SECTION_ALPHA_NUMERIC;
    },

    useSectionNumeric() {
        return this.getNumberingScheme() === numberingSchemes.SECTION_NUMERIC;
    },

    useTestSequential() {
        return this.getNumberingScheme() === numberingSchemes.TEST_SEQUENTIAL;
    },

    useNumberingPerScorableItem() {
        return this.getNumberingScheme() === numberingSchemes.PER_SCORABLE_ITEM;
    },

    getTestPlayerStyle({ settings } = storeManager.getState()) {
        const testPlayerStyle = settings.values.test_player_style;
        if (testPlayerStyle === 'default') {
            return undefined;
        }

        return testPlayerStyle;
    },

    getLockdownAdditionalResources({ settings } = storeManager.getState()) {
        return settings.values.lockdown_additional_resources;
    },

    hasLockdownAdditionalResources({ settings } = storeManager.getState()) {
        return !!settings.values.lockdown_additional_resources;
    },

    isManualStart(settings = storeManager.getState().settings) {
        return !!settings.values.start_test_manually;
    },

    isChatEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.enable_chat_during_test_taking;
    },

    isIdleStateWarningEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.enable_idle_state_warning;
    },

    isRedirectUrlEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.enable_redirect_to_url_after_submitting_demo_test;
    },

    getRedirectUrl() {
        const { settings } = storeManager.getState();
        return settings.values.redirect_to_url_after_submitting_demo_test;
    },

    getTestType({ settings } = storeManager.getState()) {
        return settings.values.atc_test_type;
    },

    isCatTestType() {
        return this.getTestType() === testTypes.CAT_TEST;
    },

    isLoftTestType() {
        return this.getTestType() === testTypes.LOFT_TEST;
    },

    isCatSyncMode() {
        const { settings } = storeManager.getState();
        return !!settings.values.enable_sync_mode_on_cat_test;
    },

    isCopyPasteDisabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.disable_copy_paste;
    },

    getCatTestAdditionalResources({ settings } = storeManager.getState()) {
        return settings.values.test_adaptive_steps;
    },

    hasCatTestAdditionalResources({ settings } = storeManager.getState()) {
        return !!settings.values.test_adaptive_steps;
    },

    allowCandidateTakeNotesDuringRemoteOralExam() {
        return !(!this.getMarketplacePropertyValue('ENABLE_NOTES_BOX_IN_IOC') && sessionReader.isWebRTCBasedRemoteSpeakingEnabled());
        // TODO: Implement this setting on platform (IA1-6160)
        // const { settings } = storeManager.getState();
        // return !!settings.values.allow_candidates_to_take_private_notes;
    },
    // Allow candidates to take private notes

    isWarningNavigatingFromIncompleteResponsesEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.warning_navigating_from_incomplete_responses;
    },

    isProgressBarQuestionsEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.progress_bar_questions;
    },

    isPromoteNextButtonOnDoneEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.promote_next_button_on_done;
    },

    isOneWayNavigationEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.enable_one_way_navigation;
    },

    isOfflineOneWayNavigationDisallowed() {
        const { settings } = storeManager.getState();
        return !!settings.values.disallow_offline_one_way_navigation;
    },

    shouldOfferAnsweredQuestionsAutocommit() {
        const { settings } = storeManager.getState();
        return !!settings.values.offer_autocommit_of_answered_questions;
    },

    shouldPageBreakOnPrintView() {
        return this.hasSettingValue(sessionReader.printParamNames.INSERT_PAGE_BREAK_BEFORE_QUESTION, true);
    },

    getWebRTCRecordingResolution(settings = storeManager.getState()?.settings) {
        if (sessionReader.isEnhancedProctoringRecordingEnabledForTest()) {
            const resolution = settings?.values?.[WebRTCRecordingStreamConstants.webrtcRecordingResolution]
            || WebRTCRecordingStreamConstants.defaultWebrtcRecordingResolutions;
            return WebRTCRecordingStreamConstants.webrtcRecordingResolutions[resolution.toLowerCase()];
        }
        return WebRTCRecordingStreamConstants.webrtcRecordingResolutions[
            [WebRTCRecordingStreamConstants.defaultWebrtcRecordingResolutions]];
    },

    getWebRTCRecordingFPS() {
        const settings = storeManager.getState()?.settings;
        if (sessionReader.isEnhancedProctoringRecordingEnabledForTest()) {
            return settings?.values?.[WebRTCRecordingStreamConstants.webrtcRecordingFPS]
            || WebRTCRecordingStreamConstants.defaultWebRtcWebCamFpsForRecordingStream;
        }
        return WebRTCRecordingStreamConstants.defaultWebRtcWebCamFpsForRecordingStream;
    },

    getWebRTCRecordingWebCamFPS() {
        const state = storeManager && typeof storeManager.getState === 'function' ? storeManager.getState() : null;
        if (state) {
            const { settings } = state;
            if (sessionReader.isEnhancedProctoringRecordingEnabledForTest()) {
                return settings?.values?.[WebRTCRecordingStreamConstants.webrtcRecordingFPS] ||
                WebRTCRecordingStreamConstants.enhancedWebRTCRecorderFPS;
            }
        }
        return WebRTCRecordingStreamConstants.defaultWebRtcWebCamFpsForRecorder;
    },

    isShowRubricForQuestionEnabled() {
        return this.getMarketplacePropertyValue('ENABLE_REUSABLE_RUBRICS')
        && this.hasSettingValue('enable_rubrics_player_display', true);
    },

    getExternalRemoteProctoringType() {
        return this.getSettingValue('externalRemoteProctoringType');
    },

    getExternalRemoteProctoringToken() {
        return this.getSettingValue('externalRemoteProctoringToken');
    },

    isExternalRemoteProctoringSecureBrowserRequired() {
        return !!this.getSettingValue('requireSecureBrowserForExternalRemoteProctoring');
    },

    getExternalBookedProctoringSessionId() {
        return this.getSettingValue('externalBookedProctoringSessionId');
    },

    getCustomCdnDynamicContent({ settings } = storeManager.getState()) {
        return marketplacePropertiesService.isEnableCustomCdnForTestContent() ? settings.values.custom_cdn_dynamic_content || '' : '';
    },

    isCustomDynamicCdnConfiguredForTest() {
        return Object.values(customDynamicCdns).includes(this.getCustomCdnDynamicContent());
    },

    isPciBasedCustomAssessment({ settings } = storeManager.getState()) {
        return marketplacePropertiesService.isPciBasedCustomAssessmentEnabled()
            && !!settings?.values?.['pci_based_assessment_delivery'] && !sessionReader.isContextReadinessCheck();
    },

    getFocusableElements(container) {
        return Array.from(container.querySelectorAll('input, button, select, textarea, [tabindex="0"]'))
            .filter(el => !el.disabled && el.tabIndex !== -1);
    },
    isPlayerTooltipReselectTextButtonEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_PLAYER_TOOLTIP_RESELECT_TEXT_BUTTON', true);
    },
    isDeleteTooltipRepositionOnScrollEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_PLAYER_DELETE_TOOLTIP_REPOSITION_ON_SCROLL', true);
    },
    getFocusOutsideTable(direction) {
        const allFocusables = Array.from(document.querySelectorAll('input, button, select, textarea, [tabindex]'))
            .filter(el => el && typeof el.closest === 'function' && el.offsetParent !== null && el.tabIndex >= 0);
        const current = document.activeElement;
        const currentIndex = allFocusables.indexOf(current);
        const currentQuestionId = current.dataset?.questionId;

        if (direction === 1) {
            for (let i = currentIndex + 1; i < allFocusables.length; i++) {
                if (currentQuestionId !== allFocusables[i].dataset?.questionId && !allFocusables[i].disabled) {
                    allFocusables[i].focus();
                    break;
                }
            }
        } else {
            for (let i = currentIndex - 1; i >= 0; i--) {
                if (currentQuestionId !== allFocusables[i].dataset?.questionId && !allFocusables[i].disabled) {
                    allFocusables[i].focus();
                    break;
                }
            }
        }
    },
    shouldHideResponseAreasForPaperSubmissions(question) {
        const isExamContext = sessionReader.isContextExam();
        const isHideResponseAreaSettingEnabled = marketplacePropertiesService.isHideResponseAreaSettingEnabled();
        const isHideResponseAreasForPaperSubmissionsEnabled = sessionReader.isHideResponseAreasForPaperSubmissionsEnabled();
        const isEssayTypeQuestion = questionReader.isExtendedText(question);

        const shouldHideResponseArea = isExamContext && isHideResponseAreaSettingEnabled
            && isHideResponseAreasForPaperSubmissionsEnabled && isEssayTypeQuestion;
        return shouldHideResponseArea;
    },
    shouldHideResponseAreaInDemoTestForPaperSubmissions(question) {
        const isHideResponseAreaSettingEnabledInDemoTest = marketplacePropertiesService.isHideResponseAreaSettingEnabledInDemoTest();
        const isHideResponseAreasForPaperSubmissionsEnabledInDemoTest = sessionReader.isHideResponseAreaInDemoTestForPaperSubmissionsEnabled();
        const isEssayTypeQuestion = questionReader.isExtendedText(question);
        const isDemoTest = sessionReader.isDemoExam();

        if (!isDemoTest) {
            return false;
        }

        const shouldHideResponseAreaInDemoTest = isDemoTest && isHideResponseAreaSettingEnabledInDemoTest
            && isHideResponseAreasForPaperSubmissionsEnabledInDemoTest && isEssayTypeQuestion;
        return shouldHideResponseAreaInDemoTest;
    },
    isPBRenderForCeqEnabled() {
        return marketplacePropertiesService.getPbRenderStyle() === marketPlaces.ceq;
    },

    /**
     * Returns paper-based rendering context for the given question model.
     * Extracts shared logic for checking paper-based rendering conditions.
     * @param {Object} questionModel - The question model containing customFields
     * Checks the PAPER_BASED_RENDER_OPTIONS custom field.
     * @returns {Object} { isEnabled: boolean, paperBasedRenderOptionValue: string }
     */

    shouldEnablePaperBasedRendering(questionModel) {
        const paperBasedRenderOptionValue = customFieldsService.getCustomFieldValue(
            questionModel?.customFields,
            customFieldsConstants.PAPER_BASED_RENDER_OPTIONS,
        ) || '';
        return {
            isEnabled: sessionReader.isPrintStylePaperBased() && this.isPBRenderForCeqEnabled(),
            paperBasedRenderOptionValue,
        };
    },
    shouldEnableDynamicLabel(questionModel) {
        const dynamicLabelValue = customFieldsService.getCustomFieldValue(
            questionModel?.customFields,
            customFieldsConstants.DYNAMIC_LABEL
        ) || '';

        const isEnabledByDynamicLabel = dynamicLabelValue === 'Yes';

        return (
            sessionReader.isPrintStylePaperBased() &&
            this.isPBRenderForCeqEnabled() &&
            isEnabledByDynamicLabel
        );
    },
    shouldUsePaperBasedImageDimensions(questionModel) {
        const { isEnabled, paperBasedRenderOptionValue } = this.shouldEnablePaperBasedRendering(questionModel);
        return isEnabled
            && paperBasedRenderOptionValue === customFieldsConstants.paperBasedRenderOptions.MCQ_HORIZONTAL_IMAGES_ON_ONE_LINE;
    },
    isTokenPositionRight(questionModel, settingTokenPosition, metadataCompareValue) {
        const { isEnabled, paperBasedRenderOptionValue } = this.shouldEnablePaperBasedRendering(questionModel);
        return isEnabled && settingTokenPosition === 'right' && paperBasedRenderOptionValue === metadataCompareValue;
    },
    isTokenPositionRightWithSplitPreview(questionModel, settingTokenPosition, metadataCompareValue) {
        const { isEnabled, paperBasedRenderOptionValue } = this.shouldEnablePaperBasedRendering(questionModel);
        return isEnabled && settingTokenPosition === 'rightWithSplitPreview' && paperBasedRenderOptionValue === metadataCompareValue;
    },
    isPaperBasedIGMSelected(questionModel) {
        const { isEnabled, paperBasedRenderOptionValue } = this.shouldEnablePaperBasedRendering(questionModel);
        return isEnabled
            && (paperBasedRenderOptionValue === customFieldsConstants.paperBasedRenderOptions.IGM_PIN_RIGHT
            || paperBasedRenderOptionValue === customFieldsConstants.paperBasedRenderOptions.IGM_TOKENS_BELOW);
    },

    /**
     * Checks if the MC discrete with one image paper-based render should be used.
     * CE-1374: Reading Multiple Choice (Discrete) with image prompt on left, options on right.
     * Only applies to questions with a SINGLE interaction (responseModels.length === 1).
     * @param {Object} questionModel - The question model containing customFields and responseModels
     * @returns {boolean} - True if the MC discrete with one image layout should be used
     */
    shouldUseMCDiscreteWithOneImageLayout(questionModel) {
        if (questionModel?.responseModels?.length !== 1) {
            return false;
        }
        const { isEnabled, paperBasedRenderOptionValue } = this.shouldEnablePaperBasedRendering(questionModel);
        return isEnabled
            && paperBasedRenderOptionValue === customFieldsConstants.paperBasedRenderOptions.MC_DISCRETE_WITH_ONE_IMAGE;
    },
    shouldUsePaperBasedWordFormation(questionModel) {
        const { isEnabled, paperBasedRenderOptionValue } = this.shouldEnablePaperBasedRendering(questionModel);
        return (isEnabled
            && paperBasedRenderOptionValue === customFieldsConstants.paperBasedRenderOptions.WORD_FORMATION_WITH_KEYWORD_LIST);
    },
    /**
     * Determines if pause/resume options should be available for test takers.
     *
     * Purpose:
     * Checks whether the marketplace property (MPP) for pause/resume is enabled
     * **and** whether it is a Listening Test with Access Arrangements enabled
     * **and** whether the current session context is an exam.
     *
     * Returns:
     * @returns {boolean} - `true` if all conditions are satisfied; otherwise `false`.
     *
     * Usage context:
     * Should be called before allowing any localStorage-based UI settings persistence
     * related to pause/resume functionality.
     * currently only for AA-Listening Test and in exam context with MPP enabled
    */
    shouldAllowPauseAndResumeOptionForTestTaker() {
        const isPauseResumeEnabled = marketplacePropertiesService.isPauseResumeOptionForTestTakerEnabled();
        const candidateCanPauseAndResume = sessionReader.isBackgroundAudioPausingAllowed();
        return isPauseResumeEnabled && sessionReader.isContextExam() && candidateCanPauseAndResume;
    },

    shouldUsePaperBasedForMCQOrientation(question) {
        const isPaperBased = sessionReader.isPrintStylePaperBased();
        return isPaperBased &&
               this.isPBRenderForCeqEnabled() &&
               questionReader.isMultipleChoiceQuestion(question);
    },

    /**
     * Checks if MCQ with image paper-based layout should be used for question number positioning.
     * This layout positions the question number beside the image (not beside the prompt).
     * Applies when: paper-based mode + CEQ MPP enabled + MCQ question with image + single interaction
     * Does NOT apply when:
     * - CE-1374 metadata is set (shouldUseMCDiscreteWithOneImageLayout handles that case)
     * - CE-1375 metadata is set (MCQ horizontal images on one line - has its own layout)
     * - MCQ has image options (images in alternatives, not in question body)
     * @param {Object} questionModel - The question model
     * @returns {boolean} - True if the MCQ with image paper-based layout should be used
     */
    shouldUsePaperBasedMCQWithImageLayout(questionModel) {
        if (!questionModel || questionModel.responseModels?.length !== 1) {
            return false;
        }
        // Don't apply if CE-1374 metadata is set - that has its own layout
        if (this.shouldUseMCDiscreteWithOneImageLayout(questionModel)) {
            return false;
        }
        // Don't apply if CE-1375 metadata is set - MCQ horizontal images on one line has its own layout
        if (this.shouldUsePaperBasedImageDimensions(questionModel)) {
            return false;
        }

        // Don't apply if MCQ has image options (images in alternatives)
        // This layout is only for images in the question body, not in options
        if (questionReader.hasImageOptions(questionModel)) {
            return false;
        }

        const isPaperBasedCeq = sessionReader.isPrintStylePaperBased() && this.isPBRenderForCeqEnabled();
        const isMCQ = questionReader.isMultipleChoiceQuestion(questionModel);
        const hasImage = questionReader.hasImageInQuestion(questionModel);

        return isPaperBasedCeq && isMCQ && hasImage;
    },
};

export default settingsReader;
